import { IResult } from '../../models/result-models';
import { IWishListItem } from '../../models/wish-list';
import {
    apiCallError,
    beginApiCall,
    endApiCall,
} from '../api/api-status-action';
import {
    createAction,
    createSlice,
    Action,
    AnyAction,
    createAsyncThunk,
    isRejectedWithValue,
} from '@reduxjs/toolkit';
import * as wishListApi from '../../api/wish-list-api';
import { rejects } from 'assert';

interface WishlistState {
    loaded: boolean;
    favorites: IWishListItem[];
    recentlyViewedProducts: IWishListItem[];
}

interface RejectedAction extends Action {
    error: Error;
}

const initialState = {
    loaded: false,
    favorites: [],
    recentlyViewedProducts: [],
} as WishlistState;

function isRejectedAction(action: AnyAction): action is RejectedAction {
    return action.type.endsWith('rejected');
}

export const loadWishlists = createAsyncThunk(
    'wishList/loadWishlists',
    async (_, thunkAPI) => {
        thunkAPI.dispatch(beginApiCall('LOAD_WISHLISTS'));
        try {
            const result = await wishListApi.loadWishlists();
            if (result.ok) {
                thunkAPI.dispatch(endApiCall('LOAD_WISHLISTS'));
                return result;
            } else {
                return thunkAPI.rejectWithValue({
                    messages: result.message,
                    errors: result.errors,
                });
            }
        } catch (error) {
            thunkAPI.dispatch(apiCallError(error as Error, 'LOAD_WISHLISTS'));
            throw error;
        }
    }
);

// todo: use this when favorites and recentlyViewedProducts are broken into separate functions
export const loadFavorites = createAsyncThunk(
    'wishList/loadFavorites',
    async (_, thunkAPI) => {
        thunkAPI.dispatch(beginApiCall('LOAD_FAVORITES'));
        try {
            const result = await wishListApi.loadWishlists(); // todo: swap this out for loadFavorites
            if (result.ok) {
                thunkAPI.dispatch(endApiCall('LOAD_FAVORITES'));
                return result;
            } else {
                return thunkAPI.rejectWithValue({
                    messages: result.message,
                    errors: result.errors,
                });
            }
        } catch (error) {
            thunkAPI.dispatch(apiCallError(error as Error, 'LOAD_FAVORITES'));
            throw error;
        }
    }
);

// todo: use this when favorites and recentlyViewedProducts are broken into separate functions
export const loadRecentlyViewedProducts = createAsyncThunk(
    'wishList/loadRecentlyViewedProducts',
    async (_, thunkAPI) => {
        thunkAPI.dispatch(beginApiCall('LOAD_RECENTLY_VIEWED_PRODUCTS'));
        try {
            const result = await wishListApi.loadWishlists(); // todo: swap this out for loadRecentlyViewedProducts
            if (result.ok) {
                thunkAPI.dispatch(endApiCall('LOAD_RECENTLY_VIEWED_PRODUCTS'));
                return result;
            } else {
                return thunkAPI.rejectWithValue({
                    messages: result.message,
                    errors: result.errors,
                });
            }
        } catch (error) {
            thunkAPI.dispatch(
                apiCallError(error as Error, 'LOAD_RECENTLY_VIEWED_PRODUCTS')
            );
            throw error;
        }
    }
);

export const addRecentlyViewedProduct = createAsyncThunk(
    'wishList/addRecentlyViewedProduct',
    async (productId: string, thunkAPI) => {
        thunkAPI.dispatch(beginApiCall('ADD_RECENTLY_VIEWED_PRODUCT'));
        try {
            const result = await wishListApi.addRecentlyViewedProduct(
                productId
            );
            if (result.ok) {
                thunkAPI.dispatch(endApiCall('ADD_RECENTLY_VIEWED_PRODUCT'));
                return result;
            } else {
                return thunkAPI.rejectWithValue({
                    messages: result.message,
                    errors: result.errors,
                });
            }
        } catch (error) {
            thunkAPI.dispatch(
                apiCallError(error as Error, 'ADD_RECENTLY_VIEWED_PRODUCT')
            );
            throw error;
        }
    }
);

export const addFavorite = createAsyncThunk(
    'wishList/addFavorite',
    async (productId: string, thunkAPI) => {
        thunkAPI.dispatch(beginApiCall('ADD_FAVORITE'));
        try {
            const result = await wishListApi.addFavorite(productId);
            if (result.ok) {
                thunkAPI.dispatch(loadWishlists());
                thunkAPI.dispatch(endApiCall('ADD_FAVORITE'));
                return result;
            } else {
                return thunkAPI.rejectWithValue({
                    messages: result.message,
                    errors: result.errors,
                });
            }
        } catch (error) {
            thunkAPI.dispatch(apiCallError(error as Error, 'ADD_FAVORITE'));
            throw error;
        }
    }
);

export const removeFavorite = createAsyncThunk(
    'wishList/removeFavorite',
    async (productId: string, thunkAPI) => {
        thunkAPI.dispatch(beginApiCall('REMOVE_FAVORITE'));
        try {
            const result = await wishListApi.removeFavorite(productId);
            if (result.ok) {
                thunkAPI.dispatch(endApiCall('REMOVE_FAVORITE'));
                return productId;
            } else {
                return thunkAPI.rejectWithValue({
                    messages: result.message,
                    errors: result.errors,
                });
            }
        } catch (error) {
            thunkAPI.dispatch(apiCallError(error as Error, 'REMOVE_FAVORITE'));
            throw error;
        }
    }
);

const wishlistSlice = createSlice({
    name: 'wishlist',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // todo: Load favorites
            // .addCase(loadFavorites.fulfilled, (state, action) => {
            //     state.favorites = action.payload.data!.favorites;
            // })
            // todo: Load recently viewed products
            // .addCase(loadRecentlyViewedProducts.fulfilled, (state, action) => {
            //     state.favorites = action.payload!.data!.recentlyViewedProducts;
            // })
            // Defaults
            .addCase(loadWishlists.fulfilled, (state, action) => {
                // todo: replace with loadFavorites and loadRecentlyViewedProducts
                state.favorites = action.payload.data!.wishLists.find(
                    (x) => x.name === 'Favorites'
                )!.wishListItems;
                state.recentlyViewedProducts =
                    action.payload.data!.wishLists.find(
                        (x) => x.name === 'RecentlyViewedProducts'
                    )!.wishListItems;
                state.loaded = true;
            })
            .addCase(loadWishlists.rejected, (state, action) => {
                state.loaded = false;
            })

            .addCase(addFavorite.fulfilled, (state, action) => {})
            .addCase(removeFavorite.fulfilled, (state, action) => {
                // We remove from wishlist without making another call to the API
                state.favorites = state.favorites.filter(
                    (x) => x.productId !== action.payload
                );
            })
            .addMatcher(
                isRejectedAction,
                // `action` will be inferred as a RejectedAction due to isRejectedAction being defined as a type guard
                (state, action) => {
                    console.error(action.error.message);
                }
            )
            .addDefaultCase((state, action) => {});
    },
});

export default wishlistSlice.reducer;
